import styled from 'styled-components';
import theme from '../../theme';

const { mobileBreakpoint } = theme;

const LessonStatus = styled.span`
  font-weight: 600;
  font-size: 1rem;
  color: ${props => {
    switch (props.status) {
      case 'Bestätigt':
        return '#3A833C'; // Green for confirmed
      case 'Ausstehend':
        return '#666'; // Orange for pending
      case 'Storniert':
        return '#e8316d'; // Red for cancelled
      case 'Abgeschlossen':
        return '#3A833C'; // Green for completed
      case 'Verschiebung angefragt':
        return '#e8316d'; // Red for reschedule requested
      case 'Geplant':
        return '#3A833C'; // Green for scheduled
      default:
        return '#666'; // Gray for unknown
    }
  }};
`;

const InformationText = styled.p`
  color: ${theme.colors.primary};
  font-size: 1rem;
  margin-bottom: 18px;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding: 16px 0;
  margin-bottom: -30px;
`;

const PaginationButton = styled.button`
  padding: 8px 12px;
  border: 1px solid ${theme.colors.accent1};
  background: ${props => props.active ? theme.colors.secondary : 'white'};
  color: ${props => props.active ? 'white' : theme.colors.primary};
  border-radius: ${theme.borderRadius.small};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 40px;
  
  &:hover {
    background: ${props => props.active ? theme.colors.secondary : theme.colors.accent1};
    color: ${props => props.active ? 'white' : theme.colors.primary};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PaginationInfo = styled.div`
  font-size: 14px;
  color: ${theme.colors.primary};
  margin: 0 16px;
`;

const ActionButton = styled.button`
  margin: 32px auto 0 auto;
  display: block;
  padding: 14px 32px;
  background: linear-gradient(135deg, ${props => props.theme.colors.secondary} 0%, #e8316d 100%);
  color: white;
  border: none;
  border-radius: ${props => props.theme.borderRadius.medium};
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  &:hover {
    background: linear-gradient(135deg, #e8316d 0%, ${props => props.theme.colors.secondary} 100%);
    transform: scale(1.01);
    box-shadow: 0 6px 20px rgba(219, 39, 98, 0.4);
  }
`;

const SecondaryButton = styled.button`
  margin: 32px auto 0 auto;
  width: 45%;
  display: block;
  padding: 14px 32px;
  background: #eee;
  color: ${theme.colors.primary};
  border: 0px solid ${theme.colors.accent1};
  border-radius: ${theme.borderRadius.medium};
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  &:hover {
    background: #f5f5f5;
    color: ${theme.colors.secondary};
    transform: scale(1.01);
  }
  @media (max-width: ${mobileBreakpoint}px) {
    min-width: unset;
    width: 100%;
  }
`;

const PrimaryButton = styled(ActionButton)`
  margin-bottom: 0px;
  width: 45%;
  @media (max-width: ${mobileBreakpoint}px) {
    min-width: unset;
    width: 100%;
    margin-top: 28px;
  }
`;

const ManageButton = styled(ActionButton)`
  margin: 0 0 0 0;
  margin-top: 10px;
  align-self: flex-end;
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.medium};
  font-size: 1rem;
  font-weight: 600;
  padding: 10px 28px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(219, 39, 98, 0.08);
`;

const AcceptButton = styled(ManageButton)`
  background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
  color: white;
  box-shadow: 0 2px 8px rgba(76, 175, 80, 0.08);
  
  &:hover {
    background: linear-gradient(135deg, #45a049 0%, #388e3c 100%);
    transform: scale(1.01);
    box-shadow: 0 6px 20px rgba(76, 175, 80, 0.4);
  }
  
  @media (max-width: ${mobileBreakpoint}px) {
    width: 100%;
    min-width: unset;
  }
`;

const SecondaryDataManageButton = styled(ManageButton)`
  background: white;
  border: 2px solid ${theme.colors.accent1};
  min-width: 140px;
  color: black;
  box-shadow: none;
  &:hover {
    background: ${theme.colors.accent1};
    color: white;
    box-shadow: none;
  }
  @media (max-width: ${mobileBreakpoint}px) {
    width: 100%;
    min-width: unset;
  }
`;

const DataManageButton = styled(ManageButton)`
  margin-top: 10px;
  min-width: 140px;
  border: 2px solid ${theme.colors.secondary};
  @media (min-width: 769px) {
    margin-top: ${props => (!props.editing ? '-44px' : '0px')};
  }
  @media (max-width: ${mobileBreakpoint}px) {
    width: 100%;
    min-width: unset;
  }
`;

// Base Badge Component
const BaseBadge = styled.div`
  width: 70px;
  min-width: 70px;
  height: 70px;
  border-radius: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  
  svg {
    width: 50px;
    height: 50px;
  }
  
  @media (max-width: ${mobileBreakpoint}px) {
    width: 60px;
    min-width: 60px;
    height: 60px;
    border-radius: 30px;
    margin-right: 14px;
    
    svg {
      width: 43px;
      height: 43px;
    }
  }
`;

// Colored Badge Base (for badges with white text)
const ColoredBadge = styled(BaseBadge)`
  color: white;
`;

// Badge Variants
const StarBadge = styled(BaseBadge)`
  background-color: ${props => props.theme.colors.accent1};
  color: ${props => props.theme.colors.primary};
`;

const AdminBadge = styled(BaseBadge)`
  background-color: #FFB6C1;
  color: ${props => props.theme.colors.primary};
`;

const LessonBadge = styled(ColoredBadge)`
  background-color: #2196F3;
`;

const PackageBadge = styled(ColoredBadge)`
  background-color: #4CAF50;
`;

const SuccessBadge = styled(ColoredBadge)`
  background-color: #4CAF50;
`;

// Complete Badge Components with SVG Icons
const StarIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 17.27L18.18 21L17 14.14L22 9.27L15.09 8.26L12 2L8.91 8.26L2 9.27L7 14.14L5.82 21L12 17.27Z"/>
  </svg>
);

const AdminIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 8L15 13.2L18 10.5L17.3 14H6.7L6 10.5L9 13.2L12 8M12 2L8.5 11L3 5L5 16H19L21 5L15.5 11L12 2Z"/>
  </svg>
);

const LessonIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M19.14,12.94c0.04-0.3,0.06-0.61,0.06-0.94c0-0.32-0.02-0.64-0.07-0.94l2.03-1.58c0.18-0.14,0.23-0.41,0.12-0.61 l-1.92-3.32c-0.12-0.22-0.37-0.29-0.59-0.22l-2.39,0.96c-0.5-0.38-1.03-0.7-1.62-0.94L14.4,2.81c-0.04-0.24-0.24-0.41-0.48-0.41 h-3.84c-0.24,0-0.43,0.17-0.47,0.41L9.25,5.35C8.66,5.59,8.12,5.92,7.63,6.29L5.24,5.33c-0.22-0.08-0.47,0-0.59,0.22L2.74,8.87 C2.62,9.08,2.66,9.34,2.86,9.48l2.03,1.58C4.84,11.36,4.8,11.69,4.8,12s0.02,0.64,0.07,0.94l-2.03,1.58 c-0.18,0.14-0.23,0.41-0.12,0.61l1.92,3.32c0.12,0.22,0.37,0.29,0.59,0.22l2.39-0.96c0.5,0.38,1.03,0.7,1.62,0.94l0.36,2.54 c0.05,0.24,0.24,0.41,0.48,0.41h3.84c0.24,0,0.44-0.17,0.47-0.41l0.36-2.54c0.5-0.24,1.03-0.56,1.62-0.94l2.39,0.96 c0.22,0.08,0.47,0,0.59-0.22l1.92-3.32c0.12-0.22,0.07-0.47-0.12-0.61L19.14,12.94z M12,15.6c-1.98,0-3.6-1.62-3.6-3.6 s1.62-3.6,3.6-3.6s3.6,1.62,3.6,3.6S13.98,15.6,12,15.6z"/>
  </svg>
);

const PackageIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M20 4H4C2.89 4 2 4.89 2 6V18C2 19.11 2.89 20 4 20H20C21.11 20 22 19.11 22 18V6C22 4.89 21.11 4 20 4M20 18H4V8H20V18Z"/>
  </svg>
);

const SuccessIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
  </svg>
);

// Complete Badge Components (Badge + Icon)
const StarBadgeWithIcon = ({ ...props }) => (
  <StarBadge {...props}>
    <StarIcon />
  </StarBadge>
);

const AdminBadgeWithIcon = ({ ...props }) => (
  <AdminBadge {...props}>
    <AdminIcon />
  </AdminBadge>
);

const LessonBadgeWithIcon = ({ ...props }) => (
  <LessonBadge {...props}>
    <LessonIcon />
  </LessonBadge>
);

const PackageBadgeWithIcon = ({ ...props }) => (
  <PackageBadge {...props}>
    <PackageIcon />
  </PackageBadge>
);

const SuccessBadgeWithIcon = ({ ...props }) => (
  <SuccessBadge {...props}>
    <SuccessIcon />
  </SuccessBadge>
);

// Message Strip Components
const MessageStrip = styled.div`
  background: ${props => props.variant === 'warning' 
    ? '#fffbe6' 
    : props.variant === 'info' 
    ? 'rgba(188, 231, 253, 0.13)' 
    : '#f5f5f5'};
  color: ${props => props.variant === 'warning' 
    ? '#b26a00' 
    : props.variant === 'info' 
    ? '#0c5298' 
    : '#666'};
  border: 1px solid ${props => props.variant === 'warning' 
    ? '#ffe58f' 
    : props.variant === 'info' 
    ? '#bce7fd' 
    : '#e0e0e0'};
  border-radius: ${theme.borderRadius.medium};
  padding: ${props => props.compact ? '10px 16px' : '16px 20px'};
  margin-bottom: 16px;
  font-weight: 500;
  font-size: ${props => props.compact ? '1.05rem' : '0.95rem'};
  display: flex;
  align-items: center;
  gap: 8px;
  line-height: 1.4;
  
`;

const MessageIcon = styled.span`
  font-size: 1.5rem;
  flex-shrink: 0;
`;

const MessageText = styled.div`
  flex: 1;
`;

const MessageLink = styled.a`
  color: inherit;
  text-decoration: underline;
  font-weight: 600;
  cursor: pointer;
  
  &:hover {
    text-decoration: none;
    opacity: 0.8;
  }
`;

export { 
  LessonStatus, 
  InformationText, 
  PaginationContainer, 
  PaginationButton, 
  PaginationInfo,
  ActionButton,
  SecondaryButton,
  PrimaryButton,
  ManageButton,
  DataManageButton,
  SecondaryDataManageButton,
  AcceptButton,
  BaseBadge,
  ColoredBadge,
  StarBadge,
  AdminBadge,
  LessonBadge,
  PackageBadge,
  SuccessBadge,
  // Complete Badge Components with Icons
  StarBadgeWithIcon,
  AdminBadgeWithIcon,
  LessonBadgeWithIcon,
  PackageBadgeWithIcon,
  SuccessBadgeWithIcon,
  // Individual Icons (for custom usage)
  StarIcon,
  AdminIcon,
  LessonIcon,
  PackageIcon,
  SuccessIcon,
  // Message Strip Components
  MessageStrip,
  MessageIcon,
  MessageText,
  MessageLink
}; 